/**
 * Screening Info Request — Add / Edit modal.
 * Angular parity with `ScreeningInfoRequestsConfigEditComponent`:
 *   - section_label (required)
 *   - one or more sub-points (Add mode): message_text (CKEditor rich-text, required) +
 *     allow_personalized_note (checkbox, defaults to true for new points)
 *   - Create posts `{ section_label, messages: [...] }`; Update flattens to `messages[0]`.
 *   - Submit guard: "Section and message are required." when section is blank or every
 *     sub-point's rich-text is empty.
 */

import { useEffect, useState } from 'react'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Plus, Trash2 } from 'lucide-react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import {
  primaryEntityActionButtonClass,
  secondaryEntityActionButtonClass,
} from '@/components/ui/entityActionButtonClasses'
import modalStyles from '@/features/application/styles/modals.module.css'
import { cn } from '@/lib/utils/helpers'
import { screeningFormSchema, type ScreeningFormValues } from '../types/schemas'
import type { ScreeningInfoRequestEditModalProps } from '../types'
import {
  createScreeningInfoRequest,
  updateScreeningInfoRequest,
} from '../services/screeningInfoRequestsService'
import { isRichTextEmpty } from '../utils/businessConfigHelpers'

export function ScreeningInfoRequestEditModal({
  isOpen,
  target,
  onClose,
  onSaved,
}: ScreeningInfoRequestEditModalProps) {
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const isEdit = Boolean(target?.screening_info_request_id)

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<ScreeningFormValues>({
    resolver: zodResolver(screeningFormSchema),
    mode: 'onChange',
    defaultValues: { section_label: '', messages: [{ message_text: '', allow_personalized_note: true }] },
  })

  const { fields, append, remove } = useFieldArray({ control, name: 'messages' })

  useEffect(() => {
    if (!isOpen) return
    setFormError(null)
    if (target) {
      reset({
        section_label: target.section_label ?? '',
        messages: [
          {
            message_text: target.message_text ?? '',
            allow_personalized_note: Boolean(Number(target.allow_personalized_note)),
          },
        ],
      })
    } else {
      reset({ section_label: '', messages: [{ message_text: '', allow_personalized_note: true }] })
    }
  }, [isOpen, target, reset])

  const onValid = async (values: ScreeningFormValues) => {
    const subPoints = values.messages
      .filter((m) => !isRichTextEmpty(m.message_text))
      .map((m) => ({
        message_text: m.message_text.trim(),
        allow_personalized_note: m.allow_personalized_note ? 1 : 0,
      }))
    const section = values.section_label.trim()

    if (!section || subPoints.length === 0) {
      setFormError('Section and message are required.')
      return
    }
    setFormError(null)
    setSubmitting(true)
    try {
      if (target) {
        await updateScreeningInfoRequest(target.screening_info_request_id, {
          section_label: section,
          message_text: subPoints[0].message_text,
          allow_personalized_note: subPoints[0].allow_personalized_note,
        })
        showSuccess('Message updated successfully.')
      } else {
        await createScreeningInfoRequest({ section_label: section, messages: subPoints })
        showSuccess('Message created successfully.')
      }
      onSaved()
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitting(false)
    }
  }

  const title = isEdit ? 'Edit Canned Message' : 'Add New Canned Message'

  return (
    <OnScreenModal isOpen={isOpen} title={title} onClose={onClose} maxWidth="max-w-3xl">
      <form onSubmit={handleSubmit(onValid)} className="min-w-0 space-y-4 p-2 sm:p-4 md:p-6">
        {formError && (
          <p className={modalStyles.formFieldError} role="alert">
            {formError}
          </p>
        )}

        <div>
          <label htmlFor="sir-section-label" className="block text-sm font-medium text-slate-700 mb-1">
            Section Title
          </label>
          <input
            id="sir-section-label"
            type="text"
            placeholder="e.g., Bank Linking Request"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            {...register('section_label')}
          />
          {errors.section_label && (
            <p className={modalStyles.formFieldError} role="alert">
              {errors.section_label.message}
            </p>
          )}
        </div>

        <div className="space-y-4">
          {fields.map((field, index) => (
            <div key={field.id} className="rounded-lg border border-slate-200 p-3 sm:p-4 space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-slate-700">
                  Message Label{fields.length > 1 ? ` ${index + 1}` : ''}
                </label>
                {fields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="p-1.5 rounded hover:bg-red-50 text-red-600"
                    aria-label="Remove sub point"
                    title="Remove sub point"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>

              <Controller
                control={control}
                name={`messages.${index}.message_text`}
                render={({ field: editorField }) => (
                  <CKEditor4
                    id={`sir-message-${index}`}
                    value={editorField.value}
                    onChange={editorField.onChange}
                    onBlur={editorField.onBlur}
                    className="rounded-lg border border-slate-300"
                  />
                )}
              />

              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  {...register(`messages.${index}.allow_personalized_note`)}
                />
                Allow Personalized Note
              </label>
            </div>
          ))}
        </div>

        {!isEdit && (
          <button
            type="button"
            onClick={() => append({ message_text: '', allow_personalized_note: true })}
            className={cn(secondaryEntityActionButtonClass, 'gap-1.5')}
          >
            <Plus className="h-4 w-4" />
            Add Sub Point
          </button>
        )}

        <div className="flex flex-wrap justify-center gap-2 border-t border-slate-200 pt-4 mt-4">
          <button
            type="submit"
            disabled={!isValid || submitting}
            className={cn(primaryEntityActionButtonClass, 'w-full sm:w-auto')}
          >
            {submitting ? 'Saving...' : 'Submit'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={cn(secondaryEntityActionButtonClass, 'w-full sm:w-auto')}
          >
            Cancel
          </button>
        </div>
      </form>
    </OnScreenModal>
  )
}
